import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TenderSubCatName, TenderBlockSize, TenderBoxMode } from 'src/models/procurements/Tender/types';
import { TenderBox, TenderSubCat } from 'src/shared';
import { Translations } from 'src/lib/I18nService/types';
import { useTenderFree } from 'src/models/procurements/TenderFree/hooks';
import { BoxFieldBoolean, BoxFieldDate, BoxFieldString, BoxFieldUrl, TenderBoxFactory } from 'src/models/procurements';
import { isBoolean } from 'src/helpers';

export const ProcurementSubCat: FC = () => {
  const { t: tenderT } = useTranslation(Translations.procurements);
  const { data } = useTenderFree();
  const subCat = data?.generalCat[TenderSubCatName.Procurement];
  const awardBox = useMemo(
    () =>
      subCat?.awardCriteria
        ? new TenderBoxFactory().create('awardCriteria', [new BoxFieldString('', subCat.awardCriteria)])
        : undefined,
    [subCat?.awardCriteria]
  );
  const questionBox = useMemo(
    () =>
      subCat?.deadlineOfQuestions
        ? new TenderBoxFactory().create('deadlineOfClarificationQuestions', [
            new BoxFieldDate('', subCat.deadlineOfQuestions)
          ])
        : undefined,
    [subCat]
  );
  const deadlineBox = useMemo(
    () =>
      subCat?.deadline ? new TenderBoxFactory().create('deadline', [new BoxFieldDate('', subCat.deadline)]) : undefined,
    [subCat?.deadline]
  );
  const publishedBox = useMemo(
    () =>
      subCat?.published
        ? new TenderBoxFactory().create('availableDate', [new BoxFieldDate('', subCat.published)])
        : undefined,
    [subCat?.published]
  );
  const procedureBox = useMemo(
    () =>
      subCat?.typeOfProcedure
        ? new TenderBoxFactory().create('typeOfProcedureBoxes', [new BoxFieldString('', subCat.typeOfProcedure)])
        : undefined,
    [subCat?.typeOfProcedure]
  );
  const frameworkBox = useMemo(
    () =>
      isBoolean(subCat?.frameworkAgreement)
        ? new TenderBoxFactory().create('frameworkAgreement', [new BoxFieldBoolean('', subCat?.frameworkAgreement)])
        : undefined,
    [subCat?.frameworkAgreement]
  );
  const linkBox = useMemo(
    () =>
      subCat?.linkForSubmittingTender
        ? new TenderBoxFactory().create('linkForSubmittingTenderBoxes', [
            new BoxFieldUrl('', subCat.linkForSubmittingTender)
          ])
        : undefined,
    [subCat?.linkForSubmittingTender]
  );
  const refNumberBox = useMemo(
    () =>
      subCat?.referenceNumber
        ? new TenderBoxFactory().create('referenceNumberBoxes', [new BoxFieldString('', subCat.referenceNumber)])
        : undefined,
    [subCat?.referenceNumber]
  );

  return (
    <>
      {subCat && (
        <TenderSubCat subCat={subCat}>
          <TenderBox
            box={questionBox}
            title={tenderT('Deadline of clarification questions')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={deadlineBox}
            title={tenderT('Submission deadline')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isFirstField
          />
          <TenderBox
            box={awardBox}
            title={tenderT('Evaluation model')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={publishedBox}
            title={tenderT('Publication date')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={procedureBox}
            title={tenderT('Type of procedure')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={frameworkBox}
            title={tenderT('Framework agreement')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={linkBox}
            title={tenderT(linkBox?.title || 'Link for submitting tender')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            actions={false}
            isFirstField
          />
          <TenderBox
            box={refNumberBox}
            title={tenderT(refNumberBox?.title || 'Reference number')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            actions={false}
            isFirstField
          />
        </TenderSubCat>
      )}
    </>
  );
};

export default ProcurementSubCat;
